import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';
import useHeadingFocuser from '../common/hooks/useHeadingFocuser';
import { accountAPI } from '../../services/redwolfSdkAPI';
import { openNotification } from '../../store/commonSlice';
import { useGoogleAnalytics } from '../common/hooks/useGoogleAnalytics';
import { useDispatch, useSelector } from 'react-redux';
import { getToken, setLocalStorage } from '../../utils/localStorage';
import Spinner from '../../components/common/components/Spinner';
import * as Sentry from '@sentry/react';
import useLogout from '../common/hooks/useLogout';

const ConfirmEmail = () => {
	const token = getToken();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();

	const queryStrings = new URLSearchParams(useLocation().search);
	const [headingRef] = useHeadingFocuser();

	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isEmailCheck, setEmailCheck] = useState(true);
	const [handleUserLogout] = useLogout();

	const transaction = Sentry.startInactiveSpan({ name: 'Confirm Email' });
	const {
		user: { userProfile }
	} = useSelector((state: any) => state);

	let intervalSeconds = 10;
	let intervalCount = 1;
	let emailCheckTimer: any;

	useEffect(() => {
		sendData();

		return () => {
			setEmailCheck(false);
			clearTimeout(emailCheckTimer);
		};
	}, []);

	const sendData = () => {
		switch (true) {
			case intervalCount < 31:
				intervalSeconds = 10;
				break;
			case intervalCount < 51:
				intervalSeconds = 15;
				break;
			case intervalCount < 61:
				intervalSeconds = 30;
				break;
			case intervalCount >= 61:
				terminateEmailVerification();
				break;
			default:
				intervalSeconds = 10;
		}

		checkIfEmailVerified();
		//Keep polling at variable intervals to check if the email has been verified
		if (isEmailCheck) {
			emailCheckTimer = setTimeout(sendData, intervalSeconds * 1000);
		}
	};

	/**
	 * Method to terminate continous polling for Email Verification Check
	 */
	const terminateEmailVerification = () => {
		setEmailCheck(false);
		emailCheckTimer && clearTimeout(emailCheckTimer);
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_EMAIL_VERIFICATION_TIMEOUT');
		setIsSpinnerLoading(true);
		handleUserLogout('login');
	};

	const sendVerificationEmail = (event: any) => {
		setIsSpinnerLoading(true);
		event.preventDefault();
		const verificationToken = {
			accessToken: token
		};
		accountAPI
			.sendVerificationEmail(verificationToken)
			.then((response: any) => {
				setIsSpinnerLoading(false);
				if (response.constructor.$metadata$.simpleName === 'Success') {
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: 'The verification email sent successfully.'
						})
					);
				} else {
					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: response.response.detail
						})
					);
					setIsSpinnerLoading(false);
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const checkIfEmailVerified = () => {
		const verificationToken = {
			accessToken: token
		};
		accountAPI
			.checkIfEmailVerified(verificationToken)
			.then((response: any) => {
				intervalCount++;
				if (response.constructor.$metadata$.simpleName === 'Success') {
					setLocalStorage('emailVerified', response.data.emailVerified);
					setEmailCheck(false);
					if (response.data.emailVerified === true) {
						setLocalStorage('requiresEmailVerification', false);
						navigate(`/terms-of-service?${queryStrings.toString()}`);
					}
				} else {
					setIsSpinnerLoading(false);
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleClick = (event: any) => {
		event.preventDefault();
		setIsSpinnerLoading(true);
		handleUserLogout('login');
	};

	return (
		<div className="confirmEmail">
			<h1 className="email-confirm-title" ref={headingRef} tabIndex={-1} aria-live="polite">
				Confirm Email
			</h1>
			<div className="confirmEmailDetails">
				<div className="confirmInfoContainer">
					<p className="confirmInfo">
						Email sent to <span className="mail-trigger">{userProfile.email}</span>
					</p>
					<p className="confirmInfo">
						Please check your inbox and click the link to verify your email and continue.
					</p>
				</div>
				<button
					type="button"
					onClick={(event) => sendVerificationEmail(event)}
					className="btn primary-button button-lg">
					<span>Resend Verification Email</span>
				</button>
				<div className="change-email-link">
					<a href="javascript:void(0)" className="link" onClick={handleClick}>
						Change Email
					</a>
				</div>
			</div>
			{isSpinnerLoading && <Spinner />}
		</div>
	);
};

export default ConfirmEmail;
